/*
 *  Copyright (c) 2024 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Config } from '../config'
import { makeRequest } from '../common/index'

const base_url = Config.admin.api_base_url
const context = Config.admin.context
const endpoint = `/${context}/publish`
const extraConfig = {
    timeout: 90000
}


/**
 *  getStatus
 *  Used to get the published status of a webpage
 *  @param {string} token
 *  @param {string} website
 *  @param {string} webpage
 */
export const getStatus = async function (token, website, webpage) {
    const method = 'GET'
    const path = `${endpoint}/${website}/status/${webpage}`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  doneEditSave
 *  Accepts a save event from the Admin Console. Occurs when the user clicks "Done Edit" in Composer.
 *  This event is used to save a version of the model.
 *  @param {string} token
 *  @param {Object} payload
 */
export const doneEditSave = async function (token, website, payload) {
    const method = 'POST'
    const path = `${endpoint}/${website}/saveVersion`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  getMyModifications
 *  Returns a list of modified webpages - by the current user. Modified means that the model is
 *  newer than the published model, or that a published model does not exit for the model.
 *  @param {string} token
 *  @param {string} website
 */
export const getMyModifications = async function (token, website) {
    const method = 'GET'
    const path = `${endpoint}/modification/${website}/my`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  getModifications
 *  Returns a list of modified webpages. Modified means that the model is newer than the published
 *  model, or that a published model does not exit for the model.
 *  @param {string} token
 *  @param {string} website
 */
export const getModifications = async function (token, website) {
    const method = 'GET'
    const path = `${endpoint}/modification/${website}`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  publishChangeSet
 *  @param {string} token
 *  @param {string} website
 *  @param {string} target
 *  @param {string} change_set_guid
 *  @param {Object} payload
 */
export const publishChangeSet = async function (token, website, target, change_set_guid, payload) {
    const method = 'POST'
    const path = `${endpoint}/${website}/target/${target}/changeset/${change_set_guid}`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  publishNow
 *  @param {string} token
 *  @param {string} website
 *  @param {string} target
 *  @param {Object} payload
 */
export const publishNow = async function (token, website, target, payload) {
    const method = 'POST'
    const path = `${endpoint}/${website}/target/${target}/publish/now`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload, extraConfig)
}

/**
 *  publishNamedQueue
 *
 *  @param {string} token
 *  @param {string} website
 *  @param {string} target
 *  @param {string} queue
 */
export const publishNamedQueue = async function (token, website, target, queue) {
    const method = 'POST'
    const path = `${endpoint}/${website}/target/${target}/queue/${queue}`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload, extraConfig)
}

/**
 *  @deprecated use publishNamedQueue
 *  publishMainQueue
 *
 *  @param {string} token
 *  @param {string} website
 *  @param {string} target
 */
export const publishMainQueue = async function (token, website, target) {
    const method = 'POST'
    const path = `${endpoint}/${website}/target/${target}/queue/main`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload, extraConfig)
}

/**
 *  @deprecated use publishNamedQueue
 *  publishModifications
 *  Publishes all modified webpages. Modified means that the model is newer than the published
 *  model, or that a published model does not exit for the model.
 *  @param {string} token
 *  @param {string} website
 *  @param {string} target
 *  @param {Object} payload
 */
export const publishModifications = async function (token, website, target, payload) {
    const method = 'POST'
    const path = `${endpoint}/${website}/target/${target}/modifications`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload, extraConfig)
}

/**
 *  getChangeSet
 *  @param {string} token
 *  @param {string} website
 *  @param {string} change_set_guid
 */
export const getChangeSet = async function (token, website, change_set_guid) {
    const method = 'GET'
    const path = `${endpoint}/${website}/changeset/${change_set_guid}`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  @deprecated - use getNamedChangeSet with 'main'
 *  getCurrentChangeSet
 *  @param {string} token
 *  @param {string} website
 */
export const getCurrentChangeSet = async function (token, website) {
    const method = 'GET'
    const path = `${endpoint}/${website}/changeset/current`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  getNamedChangeSet
 *  @param {string} token
 *  @param {string} website
 *  @param {string} queue
 */
export const getNamedChangeSet = async function (token, website, queue) {
    const method = 'GET'
    const path = `${endpoint}/${website}/queue/${queue}`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  @deprecated - use getNamedChangeSet
 *  getMainQueueChangeSet
 *  @param {string} token
 *  @param {string} website
 *  @param {string} queue
 */
export const getMainQueueChangeSet = async function (token, website) {
    const method = 'GET'
    const path = `${endpoint}/${website}/queue/main`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  addToChangeSet
 *  @param {string} token
 *  @param {string} website
 *  @param {Object} payload
 *  @deprecated
 */
export const addToChangeSet = async function (token, website, payload) {
    const method = 'POST'
    const path = `${endpoint}/${website}/changeset/add`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload)
}

export const addResourceToChangeSet = async function (token, website, change_set_guid, payload) {
    const method = 'POST'
    const path = `${endpoint}/${website}/changeset/${change_set_guid}/add`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload)
}

export const addResourceToQueue = async function (token, website, queue, payload) {
    const method = 'POST'
    const path = `${endpoint}/${website}/queue/${queue}/add`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  removeFromChangeSet
 *  @param {string} token
 *  @param {string} website
 *  @param {Object} payload
 *  @deprecated
 */
export const removeFromChangeSet = async function (token, website, payload) {
    const method = 'POST'
    const path = `${endpoint}/${website}/changeset/remove`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload)
}

export const removeResourceFromChangeSet = async function (token, website, change_set_guid, payload) {
    const method = 'POST'
    const path = `${endpoint}/${website}/changeset/${change_set_guid}/remove`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload)
}

export const removeResourceFromQueue = async function (token, website, queue, payload) {
    const method = 'POST'
    const path = `${endpoint}/${website}/queue/${queue}/remove`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  listPendingChangeSets
 *  @param {string} token
 *  @param {string} website
 */
export const listActiveChangeSets = async function (token, website) {
    const method = 'GET'
    const path = `${endpoint}/${website}/changeset/active`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  listCompletedChangeSets
 *  @param {string} token
 *  @param {string} website
 */
export const listCompletedChangeSets = async function (token, website) {
    const method = 'GET'
    const path = `${endpoint}/${website}/changeset/completed`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  listChangeSets
 *  @param {string} token
 *  @param {string} website
 */
export const listChangeSets = async function (token, website) {
    const method = 'GET'
    const path = `${endpoint}/${website}/changeset`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  createChangeSet
 *  @param {string} token
 *  @param {string} website
 *  @param {{
 *    resources: Array<{
 *        resource_guid: string;
 *        resource_type: string;
 *        change_type: string;
 *    }>
 *  }} payload
 *  @return {Promise<{guid: string}>}
 */
export const createChangeSet = async function (token, website, payload) {
    const method = 'POST'
    const path = `${endpoint}/${website}/changeset/new`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  moveResourceToChangeSet
 *  @param {string} token
 *  @param {string} website
 *  @param {string} resource_guid
 *  @return {Promise<{
 *      changed_items: number
 *  }>}
 */
export const moveResourceToChangeSet = async function (token, website, resource_guid, old_change_set_guid, new_change_set_guid) {
    const method = 'POST'
    const path = `${endpoint}/${website}/changeset/${old_change_set_guid}/resource/${resource_guid}/move`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = { new_change_set_guid }
    return await makeRequest(method, base_url, path, headers, payload)
}

export const moveResourceToQueue = async function (token, website, resource_guid, old_queue, new_queue) {
   const method = 'POST'
   const path = `${endpoint}/${website}/queue/${old_queue}/resource/${resource_guid}/move`
   const headers = {"Authorization": `Bearer ${token}`}
   const payload = { new_queue }
   return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  listResourceDependencies
 *  @param {string} token
 *  @param {string} website
 *  @param {string} resource_guid
 *  @return {Promise<Array<{
 *      resource_guid: string,
 *      resource_type: string
 *  }>>}
 */
export const listResourceDependencies = async function (token, website, resource_guid) {
    const method = 'GET'
    const path = `${endpoint}/${website}/resource/${resource_guid}/dependencies`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  listPublishHistory
 *  @param token
 *  @param website
 */
export const listPublishHistory = async function (token, website) {
    const method = 'POST'
    const path = `${endpoint}/${website}/history`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}
